import { action, computed, observable } from 'mobx';
import { UndefinedValue } from '../../../common/constants/systemValuesTranslation';
import { API_DATE_FORMAT } from '../../../common/date-manager/date-manager-constants';
import {
  PAST_PRESENT_TEMPORALITY_FILTER,
  PAST_TEMPORALITY_FILTER,
  PRESENT_TEMPORALITY_FILTER,
} from '../../../common/filter/common-filters';
import { surveyBaseColor } from '../../../common/theme/color-constants';
import {
  getNextGradientColor,
  ratioWithoutFormatting,
} from '../../../common/utilFunctions/utils';
import { HasDependencies, hashString } from '../../../common/v2/api/types';
import { ApiMasterDataQueryFilterItem } from '../../../common/v2/types';
import { getMomentDate } from '../../../common/v2/utils-date';
import {
  AnswerTypes,
  QuestionTypes,
  SurveyService,
} from './services/SurveyService';

export enum SurveyMetrics {
  AVG_SCORE = 'AvgScore',
  RESPONDENT_COUNT = 'RespondentCount',
}

export interface Cycle {
  versionId: string;
  label: string;
  startDate: string;
  endDate: string;
  color: string;
}

export enum TEMPORALITY {
  ALL = 'all',
  ACTIVE = 'active',
  INACTIVE = 'inactive',
}

export class SurveyDashboardStoreClass implements HasDependencies {
  constructor(readonly surveyService: SurveyService) {}

  public getDeps = () => [hashString([...this.surveyService.getDeps()])];

  @computed
  public get temporalityFilter(): ApiMasterDataQueryFilterItem {
    if (this.employmentTemporality === TEMPORALITY.ALL) {
      return PAST_PRESENT_TEMPORALITY_FILTER;
    } else if (this.employmentTemporality === TEMPORALITY.ACTIVE) {
      return PRESENT_TEMPORALITY_FILTER;
    }
    return PAST_TEMPORALITY_FILTER;
  }

  @observable
  public versions: string[] = [];

  @observable
  private cycles: Cycle[] = [];

  @observable
  public scores: string[] = [];

  @observable
  public scoreToColorMap: Record<string, string> = {};

  @observable
  public showCycles: Record<string, boolean> = {};

  @observable
  public metricsRadioValue: SurveyMetrics = SurveyMetrics.AVG_SCORE;

  @observable
  public employmentTemporality: TEMPORALITY = TEMPORALITY.ALL;

  public temporalityLabelMap: Record<TEMPORALITY, string> = {
    [TEMPORALITY.ALL]: 'pages:dashboards.survey.temporality.all',
    [TEMPORALITY.ACTIVE]: 'pages:dashboards.survey.temporality.active',
    [TEMPORALITY.INACTIVE]: 'pages:dashboards.survey.temporality.inactive',
  };

  @action
  public setEmploymentTemporality = (e: any) => {
    this.employmentTemporality = e.target.value;
  };

  @action
  public toggleCycle = (cycle: string) => {
    this.showCycles[cycle] = !this.showCycles[cycle];
  };

  @action
  public handleCycleChange = (selectedValues: string[]) => {
    if (selectedValues.length === 0) {
      return;
    }
    Object.keys(this.showCycles).forEach((cycle) => {
      this.showCycles[cycle] = selectedValues.includes(cycle);
    });
  };

  @action
  public loadCycles = async (
    questionTypes?: QuestionTypes[],
    answerTypes?: AnswerTypes[]
  ) => {
    const cycles = await this.surveyService.getAllCycles(
      questionTypes,
      answerTypes
    );
    this.cycles = cycles.sort((a, b) =>
      getMomentDate(a.versionId, API_DATE_FORMAT, true).isBefore(
        getMomentDate(b.versionId, API_DATE_FORMAT, true)
      )
        ? 1
        : -1
    );
    cycles.forEach(
      (cycle) => (this.showCycles[cycle.label] = cycle === cycles.first())
    );
    return cycles;
  };

  private setScoreToColorMap(
    baseColor = surveyBaseColor,
    maxGradientChange = 150
  ) {
    if (this.scores.length) {
      let currentColorGradient = baseColor;
      const gradientChangeAmount = ratioWithoutFormatting(
        maxGradientChange,
        this.scores.length
      );
      this.scoreToColorMap = Object.fromEntries(
        this.scores.map((r) => {
          const nextGradientColor = getNextGradientColor(
            currentColorGradient,
            gradientChangeAmount
          );
          currentColorGradient = nextGradientColor;
          return [r, nextGradientColor];
        })
      );
    }
  }

  @action
  public loadScores = async () => {
    const scores = await this.surveyService.getAllScores();
    scores.sort((a, b) => (a > b ? 1 : -1));
    this.scores = scores.filter((s) => s !== UndefinedValue);
    this.setScoreToColorMap();
  };

  public getAllCycles = () => {
    return this.cycles;
  };

  public getSelectedCycles = () => {
    const cycles = this.getAllCycles();
    return cycles.filter((cycle) => this.showCycles[cycle.label]);
  };
}
